import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useInView from '../../hooks/useInView';

const SERVICES = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    tag: 'SEO',
    title: 'Search Engine Optimization',
    intro: 'We improve your website\'s ability to be found by people who are already searching for what you offer — through technical precision, relevant content, and strategic link building.',
    details: [
      { label: 'Technical Audits', desc: 'Identify and fix crawlability, speed, and structural issues that prevent search engines from properly indexing your site.' },
      { label: 'Keyword Strategy', desc: 'Research and prioritize the search terms your audience actually uses — balancing volume, intent, and competition.' },
      { label: 'On-Page Optimization', desc: 'Optimize page titles, meta descriptions, headings, internal links, and content structure for maximum relevance.' },
      { label: 'Content Planning', desc: 'Build a content roadmap that captures search demand, builds authority, and brings in consistent organic traffic over time.' },
    ],
    accent: 'text-brand-blue-light',
    bg: 'bg-blue-50 border-blue-100',
    light: true,
  },
  {
    number: '02',
    icon: 'ri-instagram-line',
    tag: 'Social',
    title: 'Social Media Marketing',
    intro: 'We manage your brand\'s presence on social platforms with purpose — combining strategy, creative content, and consistent engagement to build an audience that actually cares.',
    details: [
      { label: 'Platform Strategy', desc: 'Decide which platforms are worth your attention based on where your audience is and what they expect from brands like yours.' },
      { label: 'Content Creation', desc: 'Captions, graphics, and short-form content designed to stop the scroll, communicate your brand, and drive engagement.' },
      { label: 'Community Management', desc: 'Responding to comments, DMs, and brand mentions to keep your audience engaged and your presence active and human.' },
      { label: 'Performance Tracking', desc: 'Monthly reporting on reach, engagement, follower growth, and what\'s actually working — so we can keep improving.' },
    ],
    accent: 'text-pink-500',
    bg: 'bg-pink-50 border-pink-100',
    light: false,
  },
  {
    number: '03',
    icon: 'ri-bar-chart-grouped-line',
    tag: 'Performance',
    title: 'Performance Marketing',
    intro: 'Paid campaigns structured to deliver real, trackable outcomes — not just impressions. We set up campaigns carefully, test methodically, and optimize continuously.',
    details: [
      { label: 'Campaign Structure', desc: 'Build campaign architecture with the right targeting layers, ad groups, and bidding strategies from day one.' },
      { label: 'Creative Strategy', desc: 'Develop ad concepts and messaging that resonate with your specific audience — matched to their intent and mindset.' },
      { label: 'A/B Testing', desc: 'Continuously test headlines, creatives, and audiences to find what performs and scale it intelligently.' },
      { label: 'Reporting & Optimization', desc: 'Regular review and adjustment of budgets, bids, and creatives based on what the data is telling us.' },
    ],
    accent: 'text-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
    light: true,
  },
  {
    number: '04',
    icon: 'ri-broadcast-line',
    tag: 'Media',
    title: 'Media Buying',
    intro: 'We help you reach your audience at scale across digital channels — from programmatic display and native advertising to OTT and beyond — with strategic placement decisions.',
    details: [
      { label: 'Audience Planning', desc: 'Map out who we\'re reaching, where they spend time online, and what contextual environments are most appropriate for your brand.' },
      { label: 'Channel Selection', desc: 'Choose the right mix of display, native, programmatic, OTT, or digital out-of-home channels based on your goals and budget.' },
      { label: 'Placement Optimization', desc: 'Monitor delivery and performance, adjusting placement strategies to improve quality and reduce wasted spend.' },
      { label: 'Brand Safety', desc: 'Ensure your ads appear in appropriate, quality environments that protect your brand reputation.' },
    ],
    accent: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-100',
    light: false,
  },
  {
    number: '05',
    icon: 'ri-funds-box-line',
    tag: 'Paid Ads',
    title: 'Paid Advertising',
    intro: 'Google Ads and Meta Ads, managed end-to-end. We handle setup, targeting, ad creative, and ongoing optimization — making every rupee of your ad budget work harder.',
    details: [
      { label: 'Google Ads', desc: 'Search, Display, Shopping, and YouTube campaigns — targeting people actively searching for your product or service.' },
      { label: 'Meta Ads', desc: 'Facebook and Instagram campaigns designed to reach, retarget, and convert — with creative that fits the platform.' },
      { label: 'Retargeting', desc: 'Re-engage people who\'ve already visited your site or interacted with your brand but haven\'t converted yet.' },
      { label: 'Budget Management', desc: 'Allocate your budget where it performs best — and scale the campaigns that are delivering real results.' },
    ],
    accent: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-100',
    light: true,
  },
  {
    number: '06',
    icon: 'ri-palette-line',
    tag: 'Brand',
    title: 'Branding',
    intro: 'We help you define and communicate who you are — through visual identity, messaging, and positioning that creates recognition and builds trust over time.',
    details: [
      { label: 'Brand Identity', desc: 'Logo, colour palette, typography, and visual guidelines that give your business a clear, consistent look and feel.' },
      { label: 'Brand Messaging', desc: 'The words you use to describe what you do, why it matters, and who it\'s for — kept consistent across all channels.' },
      { label: 'Positioning', desc: 'Clarify what makes your business different and how to communicate that clearly to your target audience.' },
      { label: 'Brand Guidelines', desc: 'A simple, practical reference document your team can use to maintain brand consistency as you grow.' },
    ],
    accent: 'text-yellow-500',
    bg: 'bg-yellow-50 border-yellow-100',
    light: false,
  },
  {
    number: '07',
    icon: 'ri-code-box-line',
    tag: 'Web Dev',
    title: 'Website Development',
    intro: 'Clean, fast, conversion-focused websites built to support your marketing goals — whether it\'s a landing page, business website, or a full redesign.',
    details: [
      { label: 'Business Websites', desc: 'Well-structured, professional sites that communicate what you do, who you serve, and what to do next.' },
      { label: 'Landing Pages', desc: 'Single-focus pages built to convert visitors from your campaigns — without distractions or confusion.' },
      { label: 'Speed & Performance', desc: 'Fast load times, clean code, and mobile responsiveness — because your site\'s performance directly affects your results.' },
      { label: 'SEO-Ready Structure', desc: 'Built with proper markup, metadata, and structure so your site is ready to be found from day one.' },
    ],
    accent: 'text-sky-500',
    bg: 'bg-sky-50 border-sky-100',
    light: true,
  },
];

function ServiceSection({ service }: { service: typeof SERVICES[0] }) {
  const { ref, visible } = useInView(0.1);

  return (
    <section className="py-20 md:py-24 bg-white border-b border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={ref}
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.65s ease, transform 0.65s ease' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <div className="flex items-center gap-3 mb-7">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${service.bg} transition-transform duration-300 hover:scale-110`}>
                  <i className={`${service.icon} text-xl ${service.accent}`}></i>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${service.accent} border border-current/20 px-2.5 py-1 rounded-full`}>
                  {service.tag}
                </span>
              </div>
              <div className="flex items-start gap-3 mb-5">
                <span className="font-display font-bold text-6xl leading-none select-none text-gray-200">
                  {service.number}
                </span>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-gray-900 leading-tight pt-2">
                  {service.title}
                </h2>
              </div>
              <p className="text-gray-600 text-[15px] leading-relaxed">{service.intro}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {service.details.map((d) => (
                <div key={d.label} className="rounded-xl border bg-gray-50 border-gray-100 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-sm cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${service.accent.replace('text-', 'bg-')}`}></span>
                    <p className="font-display font-semibold text-sm text-gray-800">{d.label}</p>
                  </div>
                  <p className="text-gray-600 text-[12px] leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);
  return (
    <section className="relative bg-white pt-32 pb-20 overflow-hidden border-b border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-transparent pointer-events-none"></div>
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6">
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 100ms, transform 0.7s ease 100ms' }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-blue border border-brand-blue/30 bg-brand-blue/10 px-4 py-1.5 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></span>
            What We Offer
          </span>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight max-w-3xl mb-6">
            Our Services
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed mb-10">
            Seven focused services covering every core aspect of digital marketing — from organic search to paid advertising to how your brand looks and feels online.
          </p>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map(s => (
              <span key={s.tag} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${s.accent} border border-current/20 bg-current/5 px-3 py-1.5 rounded-full`}>
                <i className={`${s.icon} text-xs`}></i>
                {s.title}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesCTA() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 bg-blue-50/50 border-t border-gray-100">
      <div ref={ref} className="max-w-site mx-auto px-4 sm:px-6 text-center" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 mb-5">Not Sure Which Service You Need?</h2>
        <p className="text-gray-600 text-[15px] max-w-lg mx-auto mb-8 leading-relaxed">
          That's completely fine. We're happy to understand your business first and then recommend what makes sense — no pressure.
        </p>
        <Link to="/contact" className="btn-primary text-base px-8 py-4">
          Let&apos;s Talk
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ServicesHero />
      {SERVICES.map(service => (
        <ServiceSection key={service.number} service={service} />
      ))}
      <ServicesCTA />
      <Footer />
    </div>
  );
}